import { useState } from 'react'
import { enregistrerMeteoActuelle, ErreurMigration0043, MESSAGE_MIGRATION_0043 } from '../../api/dispatch'
import {
  ATTRIBUTION_METEO,
  duree,
  heureBamako,
  LIEN_ATTRIBUTION_METEO,
  libelleOrigine,
  libelleSymbole,
  MESSAGE_MIGRATION_0061,
  resumer,
  validerReglagesMeteo,
  versSaisie,
  type DonneesMeteo,
  type EpisodeMeteo,
  type ModeMeteo,
  type ReglagesASaisir,
} from '../../api/meteo'
import { changerModeMeteo, enregistrerReglagesMeteo, verifierMeteoMaintenant } from '../../api/meteoAcces'
import { CONDITIONS_METEO, libelleMeteo, type ConditionMeteo } from '../../api/tarifs'
import type { Configuration } from '../../api/types'
import { fcfa } from '../Format'
import { PilotageMeteo } from './PilotageMeteo'
import { useMeteo } from './useMeteo'

/**
 * Carte « Météo en cours » de l'onglet Config.
 *
 *  • Mode AUTOMATIQUE (défaut, 0061) : l'automate décide toutes les 10 min ; on voit
 *    ce qu'il a relevé sur chaque point, qui a décidé de la condition en vigueur, et
 *    l'on peut Arrêter / Forcer. Les règles (seuils, points) se règlent ici.
 *  • Mode MANUEL : le comportement de 0043 — la bascule Normale / Pluie / Forte chaleur.
 *  • Le journal des derniers épisodes (qui, quand, pourquoi) : la réponse à une
 *    contestation de client sur un supplément pluie.
 */
export function CarteMeteo({
  config,
  sans0043,
  surMigrationAbsente,
  surErreur,
  surSucces,
}: {
  config: Configuration
  sans0043: boolean
  surMigrationAbsente: () => void
  surErreur: (m: string | null) => void
  surSucces: () => Promise<void>
}) {
  const { donnees, absente, erreur, recharger } = useMeteo(() => void surSucces())
  const actuelle = config.meteoActuelle
  const bareme = actuelle ? config.bonusMeteo.find((b) => b.condition === actuelle) : undefined
  const [enCours, setEnCours] = useState<string | null>(null)
  const [msg, setMsg] = useState<{ texte: string; erreur: boolean } | null>(null)

  async function agir(cle: string, f: () => Promise<void>, ok?: string) {
    if (enCours) return
    setEnCours(cle)
    setMsg(null)
    try {
      await f()
      if (ok) setMsg({ texte: ok, erreur: false })
      surErreur(null)
      await Promise.all([recharger(), surSucces()])
    } catch (err) {
      if (err instanceof ErreurMigration0043) surMigrationAbsente()
      else setMsg({ texte: err instanceof Error ? err.message : String(err), erreur: true })
    } finally {
      setEnCours(null)
    }
  }

  const mode: ModeMeteo = donnees?.reglages.mode ?? 'manuel'
  const resume = donnees && actuelle ? resumer(actuelle, donnees) : null

  return (
    <div className="card carte-meteo">
      <div className="card-header">Météo en cours</div>
      <div className="card-body">
        {sans0043 ? (
          <p className="avis avis-info">{MESSAGE_MIGRATION_0043}</p>
        ) : (
          <>
            {donnees && (
              <div className="meteo-ligne">
                <span className="meteo-titre">Mode :</span>
                <div className="seg" role="radiogroup" aria-label="Mode météo">
                  {(['auto', 'manuel'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={mode === m}
                      className={mode === m ? 'on' : ''}
                      disabled={enCours !== null}
                      onClick={() => mode !== m && void agir('mode', () => changerModeMeteo(m))}
                    >
                      {m === 'auto' ? 'Automatique' : 'Manuel'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {absente && <p className="avis avis-info">{MESSAGE_MIGRATION_0061}</p>}
            {erreur && <p className="avis avis-err">{erreur}</p>}

            {mode === 'manuel' || !donnees ? (
              <div className="meteo-ligne" style={{ marginTop: donnees ? 14 : 0 }}>
                <span className="meteo-titre">Météo en cours :</span>
                <div className="seg" role="radiogroup" aria-label="Météo en cours">
                  {CONDITIONS_METEO.map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={c === actuelle}
                      className={c === actuelle ? 'on' : ''}
                      disabled={enCours !== null}
                      onClick={() => c !== actuelle && void agir(`m-${c}`, () => enregistrerMeteoActuelle(c))}
                    >
                      {libelleMeteo(c)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              resume && (
                <div className="meteo-auto">
                  <div className="meteo-ligne">
                    <span className={`badge ${actuelle === 'normal' ? 'badge-gray' : 'badge-orange'}`}>{resume.libelle}</span>
                    <span className="meteo-source">
                      {resume.texteSource}
                      {resume.depuis && actuelle !== 'normal' && ` · depuis ${heureBamako(resume.depuis)} (${duree(resume.depuis, null)})`}
                    </span>
                  </div>
                  <PilotageMeteo resume={resume} surFait={async () => { await recharger(); await surSucces() }} />
                  {resume.avertissement && <p className="avis avis-warn">{resume.avertissement}</p>}
                  <Releves donnees={donnees} />
                  <div className="meteo-ligne meteo-pied">
                    <span className="aide" style={{ margin: 0 }}>
                      Dernier relevé {heureBamako(donnees.etat.dernierReleve)} · prochaine vérification{' '}
                      {heureBamako(donnees.etat.prochaineVerification)} (heure Bamako)
                    </span>
                    <button
                      className="btn btn-outline btn-sm"
                      disabled={enCours !== null}
                      onClick={() => void agir('verif', verifierMeteoMaintenant, 'Relevé effectué.')}
                    >
                      {enCours === 'verif' ? 'Vérification…' : 'Vérifier maintenant'}
                    </button>
                  </div>
                </div>
              )
            )}

            <div className="meteo-chiffres">
              {bareme ? (
                <>
                  <div>
                    <span className="meteo-lbl">Le client paie en plus</span>
                    <b>{bareme.supplementClient ? `+${fcfa(bareme.supplementClient)}` : '0 FCFA'}</b>
                  </div>
                  <div>
                    <span className="meteo-lbl">Le livreur touche en plus</span>
                    <b>{bareme.bonusLivreur ? `+${fcfa(bareme.bonusLivreur)}` : '0 FCFA'}</b>
                  </div>
                </>
              ) : (
                <span className="avis avis-info">
                  Aucun barème défini pour « {actuelle ? libelleMeteo(actuelle) : '—'} » : réglez-le dans le Barème météo, plus bas.
                </span>
              )}
            </div>
            {msg && <p className={`avis ${msg.erreur ? 'avis-err' : 'avis-info'}`}>{msg.texte}</p>}
            <p className="aide">
              Le supplément s'applique à <b>toutes les nouvelles commandes</b> jusqu'au retour à « Normale ». Les commandes déjà
              passées gardent leur prix.
              {bareme?.premiumExempt && bareme.supplementClient > 0 ? ' Les abonnés Premium ne paient pas le supplément client.' : ''}
            </p>

            {donnees && <Regles donnees={donnees} surEnregistre={async () => { await recharger() }} />}
            {donnees && <Journal episodes={donnees.episodes} noms={donnees.noms} />}
            {donnees && (
              <p className="aide">
                <a href={LIEN_ATTRIBUTION_METEO} target="_blank" rel="noreferrer">{ATTRIBUTION_METEO}</a> (licence CC BY 4.0),
                prévisions Locationforecast relevées par le serveur.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** Ce que l'automate a lu sur chaque point au dernier passage. */
function Releves({ donnees }: { donnees: DonneesMeteo }) {
  const { releves } = donnees.etat
  if (!releves.length) return <p className="aide">Aucun relevé pour l'instant : le premier passage de l'automate est attendu.</p>
  return (
    <table className="meteo-table">
      <thead>
        <tr>
          <th>Point</th>
          <th>Ciel (heure en cours)</th>
          <th>Pluie</th>
          <th>Temp.</th>
        </tr>
      </thead>
      <tbody>
        {releves.map((r) => (
          <tr key={r.code} className={r.pluie ? 'pluvieux' : ''}>
            <td>
              {r.nom}
              {r.code === donnees.reglages.pointChaleur ? ' (chaleur)' : ''}
            </td>
            {r.ok ? (
              <>
                <td>{libelleSymbole(r.symbole).libelle}</td>
                <td>{r.precipitationMm != null ? `${String(r.precipitationMm).replace('.', ',')} mm` : '—'}</td>
                <td>{r.temperatureC != null ? `${Math.round(r.temperatureC)} °C` : '—'}</td>
              </>
            ) : (
              <td colSpan={3} style={{ color: 'var(--danger)' }}>
                Indisponible ({r.erreur ?? 'erreur'})
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** Les règles de l'automate (droit `gerer_config` ; la base refuse sinon). */
function Regles({ donnees, surEnregistre }: { donnees: DonneesMeteo; surEnregistre: () => Promise<void> }) {
  const [ouvert, setOuvert] = useState(false)
  const [s, setS] = useState<ReglagesASaisir>(() => versSaisie(donnees.reglages))
  const [erreurs, setErreurs] = useState<string[]>([])
  const [etat, setEtat] = useState<string | null>(null)
  const maj = (p: Partial<ReglagesASaisir>) => setS((v) => ({ ...v, ...p }))
  const champ = (libelle: string, cle: keyof ReglagesASaisir, unite: string) => (
    <label className="config-field">
      <span className="cf-label">{libelle}</span>
      <span>
        <input style={{ width: 80 }} value={String(s[cle])} onChange={(e) => maj({ [cle]: e.target.value } as Partial<ReglagesASaisir>)} />{' '}
        {unite}
      </span>
    </label>
  )

  async function enregistrer() {
    const v = validerReglagesMeteo(s)
    setErreurs(v.erreurs)
    if (!v.ligne) return
    setEtat('…')
    try {
      await enregistrerReglagesMeteo(v.ligne)
      setEtat('Réglages enregistrés : ils s’appliquent dès le prochain relevé.')
      await surEnregistre()
    } catch (e) {
      setEtat(null)
      setErreurs([e instanceof Error ? e.message : String(e)])
    }
  }

  return (
    <details className="meteo-regles" open={ouvert} onToggle={(e) => setOuvert((e.target as HTMLDetailsElement).open)}>
      <summary>Règles de l'automate</summary>
      <p className="aide">
        Pluie si au moins <b>{donnees.reglages.pluiePointsMin} points sur {donnees.reglages.points.length}</b> annoncent ≥{' '}
        {String(donnees.reglages.pluieSeuilMm).replace('.', ',')} mm sur l'heure ou un symbole de pluie / averse / orage ; fin après{' '}
        {donnees.reglages.pluieFinMinutes} min sans pluie sur tous les points. Forte chaleur à {donnees.reglages.chaleurActivationC} °C,
        fin sous {donnees.reglages.chaleurDesactivationC} °C. La pluie prime. Après {donnees.reglages.echecsMax} relevés en échec, retour à
        Normale.
      </p>
      {champ('Seuil de pluie', 'pluieSeuilMm', 'mm / h')}
      {champ('Points sous la pluie pour activer', 'pluiePointsMin', `sur ${s.points.length}`)}
      {champ('Fin de pluie après', 'pluieFinMinutes', 'min sans pluie')}
      {champ('Forte chaleur à partir de', 'chaleurActivationC', '°C')}
      {champ('Fin de forte chaleur sous', 'chaleurDesactivationC', '°C')}
      {champ('Échecs avant retour à Normale', 'echecsMax', 'relevés')}
      <label className="config-field">
        <span className="cf-label">Point qui décide de la chaleur</span>
        <select value={s.pointChaleur} onChange={(e) => maj({ pointChaleur: e.target.value })}>
          {s.points.map((p) => (
            <option key={p.code} value={p.code}>
              {p.nom}
            </option>
          ))}
        </select>
      </label>
      <label className="config-field">
        <span className="cf-label">Prévenir les livreurs (« Bonus pluie actif »)</span>
        <input type="checkbox" checked={s.pushLivreurs} onChange={(e) => maj({ pushLivreurs: e.target.checked })} />
      </label>
      <table className="meteo-table">
        <thead>
          <tr>
            <th>Point</th>
            <th>Latitude</th>
            <th>Longitude</th>
          </tr>
        </thead>
        <tbody>
          {s.points.map((p, i) => (
            <tr key={p.code}>
              <td>
                <input
                  value={p.nom}
                  onChange={(e) => maj({ points: s.points.map((x, j) => (j === i ? { ...x, nom: e.target.value } : x)) })}
                />
              </td>
              <td>
                <input
                  style={{ width: 90 }}
                  value={p.lat}
                  onChange={(e) => maj({ points: s.points.map((x, j) => (j === i ? { ...x, lat: e.target.value } : x)) })}
                />
              </td>
              <td>
                <input
                  style={{ width: 90 }}
                  value={p.lon}
                  onChange={(e) => maj({ points: s.points.map((x, j) => (j === i ? { ...x, lon: e.target.value } : x)) })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {erreurs.length > 0 && (
        <div className="avis avis-err">
          <ul>
            {erreurs.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {etat && <p className="aide">{etat}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-accent btn-sm" onClick={() => void enregistrer()}>
          Enregistrer les règles
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => { setS(versSaisie(donnees.reglages)); setErreurs([]); setEtat(null) }}>
          Annuler
        </button>
      </div>
    </details>
  )
}

const nomDe = (id: string | null, o: string, noms: Record<string, string>) =>
  id ? (noms[id] ?? 'Membre du staff') : o

/** Derniers épisodes : de quand à quand, qui a décidé, pourquoi. */
function Journal({ episodes, noms }: { episodes: EpisodeMeteo[]; noms: Record<string, string> }) {
  return (
    <details className="meteo-regles">
      <summary>Derniers épisodes ({episodes.length})</summary>
      {episodes.length === 0 ? (
        <p className="aide">Aucun épisode de pluie ou de forte chaleur enregistré.</p>
      ) : (
        <table className="meteo-table">
          <thead>
            <tr>
              <th>Condition</th>
              <th>Début</th>
              <th>Fin</th>
              <th>Durée</th>
              <th>Décidé par</th>
            </tr>
          </thead>
          <tbody>
            {episodes.map((e) => (
              <tr key={e.id}>
                <td>{libelleMeteo(e.condition as ConditionMeteo)}</td>
                <td title={e.motifDebut ?? ''}>
                  {new Date(e.debut).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Bamako' })}
                  {e.motifDebut && <div className="meteo-motif">{e.motifDebut}</div>}
                </td>
                <td title={e.motifFin ?? ''}>
                  {e.fin ? heureBamako(e.fin) : <span className="badge badge-orange">en cours</span>}
                  {e.motifFin && <div className="meteo-motif">{e.motifFin}</div>}
                </td>
                <td>{duree(e.debut, e.fin)}</td>
                <td>
                  {nomDe(e.debutPar, libelleOrigine(e.origineDebut), noms)}
                  {e.fin && ` → ${nomDe(e.finPar, libelleOrigine(e.origineFin), noms)}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </details>
  )
}
